import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CLIPS } from "@besenliga/anim";
import type { AthleteSpec } from "@besenliga/athlete-three";
import { randomAppearance } from "@besenliga/content";
import {
  BUILDS,
  EXAMPLE_KITS,
  FACE_SHAPES,
  HAIR_COLORS,
  HAIR_STYLES,
  HEIGHTS,
  PROFILE_LIMITS,
  SEXES,
  SKIN_TONES,
  STATS,
  STAT_LABELS,
  BASE_CAP,
  profileProblems,
  seedRng,
  statCaps,
  type Height,
  type Position,
  type Sex,
} from "@besenliga/shared";
import { AthleteStage, type StageMode } from "./stage";
import { KIT_LABELS, POSITION_INFO, PROBLEM_TEXT, SAVE_ERROR_TEXT, hexCss } from "./options";
import {
  KIT_IDS,
  ROSTER_MAX,
  browserStore,
  deleteAthlete,
  loadRoster,
  saveAthlete,
  type SavedAthlete,
} from "./roster";

type Draft = Omit<SavedAthlete, "savedAt">;

const MODELS_URL = `${import.meta.env.BASE_URL}modelos/`;

function randomSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] ?? 1;
}

function newId(): string {
  return crypto.randomUUID?.() ?? `a${randomSeed().toString(36)}${Date.now().toString(36)}`;
}

function freshDraft(): Draft {
  const sex: Sex = randomSeed() % 2 === 0 ? "homem" : "mulher";
  const [, appearance] = randomAppearance(seedRng(randomSeed()), sex);
  return {
    id: newId(),
    kit: "corujas-campinas",
    name: "",
    number: 10,
    position: "artilheiro",
    sex,
    height: "medio",
    build: "medio",
    ...appearance,
  };
}

function withoutSavedAt(a: SavedAthlete): Draft {
  const draft: Partial<SavedAthlete> = { ...a };
  delete draft.savedAt;
  return draft as Draft;
}

const sameDraft = (a: Draft, b: SavedAthlete) =>
  (Object.keys(a) as (keyof Draft)[]).every((k) => a[k] === b[k]);

export function Creator() {
  const store = useMemo(() => browserStore(), []);
  const [roster, setRoster] = useState<SavedAthlete[]>(() => loadRoster(store));
  const [draft, setDraft] = useState<Draft>(() => {
    const first = loadRoster(store)[0];
    if (!first) return freshDraft();
    return withoutSavedAt(first);
  });
  const [mode, setMode] = useState<StageMode>("pe");
  const [status, setStatus] = useState<{ kind: "ok" | "erro"; text: string } | null>(null);
  const [triedSave, setTriedSave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [stageState, setStageState] = useState<"carregando" | "pronto" | "erro">("carregando");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<AthleteStage | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setStatus(null);
  };

  const spec: AthleteSpec = useMemo(
    () => ({
      sexo: draft.sex,
      altura: draft.height,
      biotipo: draft.build,
      posicao: draft.position,
      skinTone: draft.skinTone,
      hairStyle: draft.hairStyle,
      hairColor: draft.hairColor,
      faceShape: draft.faceShape,
      beard: draft.beard,
      goggles: draft.goggles,
    }),
    [draft],
  );
  const kit = EXAMPLE_KITS[draft.kit];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let stage: AthleteStage;
    try {
      stage = new AthleteStage(canvas);
    } catch {
      setStageState("erro");
      return;
    }
    stageRef.current = stage;
    let alive = true;
    stage.load(MODELS_URL).then(
      () => alive && setStageState("pronto"),
      () => alive && setStageState("erro"),
    );
    return () => {
      alive = false;
      stage.dispose();
      stageRef.current = null;
    };
  }, []);

  useEffect(() => stageRef.current?.setAthlete(spec, kit), [spec, kit]);
  useEffect(() => stageRef.current?.setMode(mode), [mode]);

  const problems = profileProblems(draft);
  const saved = roster.find((a) => a.id === draft.id);
  const dirty = !saved || !sameDraft(draft, saved);
  const caps = statCaps({ sex: draft.sex, height: draft.height, build: draft.build });
  const info = POSITION_INFO[draft.position];

  function save() {
    setTriedSave(true);
    const result = saveAthlete(store, roster, draft, new Date());
    if (!result.ok) {
      setStatus({
        kind: "erro",
        text: SAVE_ERROR_TEXT[result.reason],
      });
      return;
    }
    setRoster(result.roster);
    setDraft((d) => ({ ...d, name: result.athlete.name }));
    setTriedSave(false);
    setStatus({ kind: "ok", text: `${result.athlete.name} foi salvo neste navegador.` });
  }

  function open(a: SavedAthlete) {
    setDraft(withoutSavedAt(a));
    setStatus(null);
    setTriedSave(false);
    setConfirmDelete(null);
  }

  function remove(id: string) {
    if (confirmDelete !== id) {
      setConfirmDelete(id);
      return;
    }
    const next = deleteAthlete(store, roster, id);
    setConfirmDelete(null);
    if (!next) {
      setStatus({ kind: "erro", text: SAVE_ERROR_TEXT.armazenamento });
      return;
    }
    setRoster(next);
    if (id === draft.id) setDraft(freshDraft());
  }

  function shuffleLook() {
    const [, appearance] = randomAppearance(seedRng(randomSeed()), draft.sex);
    setDraft((d) => ({ ...d, ...appearance }));
    setStatus(null);
  }

  const displayName = draft.name.trim() || "Sem nome";

  return (
    <div className="creator">
      <section className="stage" aria-label="Prévia 3D do atleta">
        <canvas
          ref={canvasRef}
          className="stage-canvas"
          tabIndex={0}
          aria-label="Atleta em 3D. Arraste ou use as setas para girar."
        />
        <div className="nameplate" aria-hidden="true">
          <span className="nameplate-name">{displayName}</span>
          <span className="nameplate-number">{draft.number}</span>
        </div>
        {stageState !== "pronto" && (
          <p className="stage-message" role="status">
            {stageState === "carregando"
              ? "Carregando o atleta…"
              : "Não foi possível mostrar o atleta em 3D neste navegador. A ficha continua funcionando."}
          </p>
        )}
        <div className="stage-controls">
          <div className="segmented" role="group" aria-label="Pose da prévia">
            <button type="button" aria-pressed={mode === "pe"} onClick={() => setMode("pe")}>
              Em pé
            </button>
            <button type="button" aria-pressed={mode === "vassoura"} onClick={() => setMode("vassoura")}>
              Na vassoura
            </button>
          </div>
          <div className="rotate">
            <button
              type="button"
              aria-label="Girar para a esquerda"
              onClick={() => stageRef.current?.rotate(-Math.PI / 4)}
            >
              ↺
            </button>
            <button
              type="button"
              aria-label="Girar para a direita"
              onClick={() => stageRef.current?.rotate(Math.PI / 4)}
            >
              ↻
            </button>
          </div>
          {mode === "vassoura" && (
            <button type="button" className="ghost" onClick={() => stageRef.current?.play(CLIPS[info.clip])}>
              {info.move}
            </button>
          )}
        </div>
      </section>

      <section className="sheet" aria-label="Ficha do atleta">
        <header className="sheet-head">
          <h1>Criar atleta</h1>
          <p>Seu atleta para o modo clube. Ele joga sempre na posição que você escolher aqui.</p>
          <a className="ghost sheet-link" href="#">
            Menu
          </a>{" "}
          <a className="ghost sheet-link" href="#partida">
            Jogar uma partida
          </a>{" "}
          <a className="ghost sheet-link" href="#voo">
            Voar com seu atleta
          </a>
        </header>

        <Group title="Identidade">
          <div className="row two">
            <label className="field">
              <span>Nome na camisa</span>
              <input
                value={draft.name}
                maxLength={PROFILE_LIMITS.nameMax + 4}
                placeholder="Ex.: Inês Barreto"
                autoComplete="off"
                aria-invalid={triedSave && problems.some((p) => p.startsWith("nome"))}
                onChange={(e) => set("name", e.target.value)}
              />
            </label>
            <label className="field number">
              <span>Número</span>
              <input
                type="number"
                inputMode="numeric"
                min={PROFILE_LIMITS.numberMin}
                max={PROFILE_LIMITS.numberMax}
                value={Number.isNaN(draft.number) ? "" : draft.number}
                aria-invalid={triedSave && problems.includes("numero")}
                onChange={(e) => set("number", e.target.value === "" ? Number.NaN : Number(e.target.value))}
              />
            </label>
          </div>
          <div className="field">
            <span id="lbl-posicao">Posição</span>
            <div className="positions" role="radiogroup" aria-labelledby="lbl-posicao">
              {(Object.keys(POSITION_INFO) as Position[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={draft.position === p}
                  className="position"
                  onClick={() => set("position", p)}
                >
                  <strong>{POSITION_INFO[p].label}</strong>
                  <small>{POSITION_INFO[p].role}</small>
                  <small className="gear">Usa {POSITION_INFO[p].gear}</small>
                </button>
              ))}
            </div>
          </div>
        </Group>

        <Group title="Corpo">
          <Choice label="Sexo" value={draft.sex} options={labelled(SEXES)} onChange={(v) => set("sex", v)} />
          <Choice
            label="Altura"
            value={draft.height}
            options={(Object.keys(HEIGHTS) as Height[]).map((id) => ({
              id,
              label: `${HEIGHTS[id].label} · ${HEIGHTS[id].meters?.toFixed(2).replace(".", ",")} m`,
            }))}
            onChange={(v) => set("height", v)}
          />
          <Choice
            label="Biotipo"
            value={draft.build}
            options={labelled(BUILDS)}
            onChange={(v) => set("build", v)}
          />
          <div className="caps" aria-label="Teto de cada atributo para este corpo">
            <p className="hint">
              Teto de cada atributo. Nenhum corpo é melhor: o que sobe num atributo desce em outro.
            </p>
            <ul>
              {STATS.map((s) => {
                const delta = caps[s] - BASE_CAP;
                return (
                  <li key={s} data-delta={delta > 0 ? "up" : delta < 0 ? "down" : "zero"}>
                    <span className="cap-label">{STAT_LABELS[s]}</span>
                    <span className="cap-bar" aria-hidden="true">
                      <span style={{ width: `${caps[s]}%` }} />
                    </span>
                    <span className="cap-value">
                      {caps[s]}
                      {delta !== 0 && <em>{delta > 0 ? `+${delta}` : delta}</em>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </Group>

        <Group
          title="Aparência"
          action={
            <button type="button" className="ghost" onClick={shuffleLook}>
              Sortear aparência
            </button>
          }
        >
          <Swatches
            label="Pele"
            value={draft.skinTone}
            options={SKIN_TONES}
            onChange={(v) => set("skinTone", v)}
          />
          <Choice
            label="Cabelo"
            value={draft.hairStyle}
            options={HAIR_STYLES}
            onChange={(v) => set("hairStyle", v)}
          />
          {draft.position === "goleiro" && draft.hairStyle !== "careca" && (
            <p className="hint">O capacete do goleiro cobre o cabelo em campo.</p>
          )}
          <Swatches
            label="Cor do cabelo"
            value={draft.hairColor}
            options={HAIR_COLORS}
            onChange={(v) => set("hairColor", v)}
          />
          <Choice
            label="Rosto"
            value={draft.faceShape}
            options={FACE_SHAPES}
            onChange={(v) => set("faceShape", v)}
          />
          <div className="row two toggles">
            <Toggle label="Barba" checked={draft.beard} onChange={(v) => set("beard", v)} />
            <Toggle label="Óculos de voo" checked={draft.goggles} onChange={(v) => set("goggles", v)} />
          </div>
        </Group>

        <Group title="Uniforme de treino">
          <div className="kits" role="radiogroup" aria-label="Uniforme de treino">
            {KIT_IDS.map((id) => {
              const k = EXAMPLE_KITS[id];
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={draft.kit === id}
                  className="kit"
                  onClick={() => set("kit", id)}
                >
                  <span className="kit-chip" aria-hidden="true">
                    <span style={{ background: hexCss(k.primary) }} />
                    <span style={{ background: hexCss(k.secondary) }} />
                    <span style={{ background: hexCss(k.shorts) }} />
                  </span>
                  {KIT_LABELS[id]}
                </button>
              );
            })}
          </div>
          <p className="hint">Quando você entrar num clube, o atleta veste o uniforme do clube.</p>
        </Group>

        <div className="save-bar">
          {triedSave && problems.length > 0 && (
            <ul className="problems" role="alert">
              {problems.map((p) => (
                <li key={p}>{PROBLEM_TEXT[p]}</li>
              ))}
            </ul>
          )}
          {status && (
            <p className={`status ${status.kind}`} role={status.kind === "erro" ? "alert" : "status"}>
              {status.text}
            </p>
          )}
          <div className="save-actions">
            <button type="button" className="primary" onClick={save} disabled={!dirty && !!saved}>
              {saved ? (dirty ? "Salvar alterações" : "Salvo") : "Salvar atleta"}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setDraft(freshDraft());
                setStatus(null);
                setTriedSave(false);
              }}
            >
              Novo atleta
            </button>
          </div>
        </div>

        <Group title={`Seus atletas (${roster.length} de ${ROSTER_MAX})`}>
          {roster.length === 0 ? (
            <p className="hint">Nenhum atleta salvo ainda. Monte o seu e toque em Salvar atleta.</p>
          ) : (
            <ul className="roster">
              {roster.map((a) => (
                <li key={a.id} aria-current={a.id === draft.id}>
                  <button type="button" className="roster-open" onClick={() => open(a)}>
                    <span
                      className="roster-number"
                      style={{ background: hexCss(EXAMPLE_KITS[a.kit].primary) }}
                    >
                      {a.number}
                    </span>
                    <span>
                      <strong>{a.name}</strong>
                      <small>
                        {POSITION_INFO[a.position].label} · {HEIGHTS[a.height].label} ·{" "}
                        {BUILDS[a.build].label}
                      </small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={confirmDelete === a.id ? "danger" : "ghost"}
                    onClick={() => remove(a.id)}
                    onBlur={() => setConfirmDelete((c) => (c === a.id ? null : c))}
                  >
                    {confirmDelete === a.id ? "Confirmar exclusão" : "Excluir"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Group>
      </section>
    </div>
  );
}

function labelled<K extends string>(record: Readonly<Record<K, { label: string }>>) {
  return (Object.keys(record) as K[]).map((id) => ({ id, label: record[id].label }));
}

function Group({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <fieldset className="group">
      <legend>{title}</legend>
      {action && <div className="group-action">{action}</div>}
      {children}
    </fieldset>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="field">
      <span>{label}</span>
      <div className="chips" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={o.id === value}
            onClick={() => onChange(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Swatches<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { id: T; label: string; hex: number }[];
  onChange: (v: T) => void;
}) {
  const current = options.find((o) => o.id === value);
  return (
    <div className="field">
      <span>
        {label}
        {current && <em className="field-value">{current.label}</em>}
      </span>
      <div className="swatches" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={o.id === value}
            aria-label={o.label}
            title={o.label}
            style={{ background: hexCss(o.hex) }}
            onClick={() => onChange(o.id)}
          />
        ))}
      </div>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
